import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as qs from 'query-string';
import { makeStyles } from '@material-ui/core';
import AddAlertIcon from '@material-ui/icons/AddAlert';
import cx from 'classnames';

import { Button, ButtonProps, Link, NoSSR, units } from '@ha/dsl';

import { AlarmBell1Light } from '@ha/icons';
import { useIntl } from 'ha/i18n';
import { useMediaQuery } from 'ha/helpers/MediaQuery';
import { useLocation } from 'ha/helpers/router';
import { Actions as NavbarActions } from 'ha/modules/NavBar/actions';
import Track from 'ha/components/track/SmartTrack';
import { getIsAuthenticated, getUserId } from 'ha/modules/AuthLogic/selectors';
import { useTrackEvent } from 'ha/modules/Analytics/helpers/TrackEvent';
import { SearchAlertChannel, SearchAlertFrequency } from 'ha/services/bell';
import { updateUser } from 'ha/modules/AuthLogic/actions';
import { PhoneNumberProps } from 'ha/ui/next/PhoneInput';

import { send as showAlert } from 'ha/modules/Alertbar/actions';
import { AlertType } from 'ha/modules/Alertbar/types';
import { SearchAlertDialog } from 'ha/modules/SearchAlerts';

import {
  hasSearchAlert,
  isSearchAlertDisabled,
  getSearchAlert,
} from '../../selectors/searchAlertSelectors';
import SearchAlertResponseDialog from './SearchAlertResponseDialog';
import useQueryParams from '../../hooks/useQueryParams';
import {
  getNumberOfListings,
  getCityCanonical,
  getCityName,
} from '../../selectors';
import { upsertSearchAlert } from '../../actions';

interface Props {
  size?: ButtonProps['size'];
  fullWidth?: ButtonProps['fullWidth'];
  variant?: 'link' | 'button';
}

const useStyles = makeStyles(theme => ({
  button: {
    height: units.rem(2.5),
    width: units.rem(2.5),
    border: '1px solid',
    borderRadius: '4px',
    padding: 0,
  },
  icon: {
    height: units.rem(1.325),
    width: units.rem(1.325),
  },
  linkIcon: {
    position: 'relative',
    top: '2px',
    fontSize: 'inherit',
    color: theme.palette.secondary.dark,
  },
  linkIconDisabled: {
    color: theme.palette.grey[400],
  },
  link: {
    color: theme.palette.secondary.dark,
  },
  linkDisabled: {
    color: theme.palette.grey[400],
    cursor: 'default',
  },
}));

type DialogType = 'main' | 'response-create' | 'response-update' | undefined;

export const SearchAlert: React.FC<Props> = ({ fullWidth, size, variant }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { T } = useIntl();
  const { md } = useMediaQuery();
  const location = useLocation();
  const trackEvent = useTrackEvent();
  const { removeUpdateSearchAlertParam } = useQueryParams();

  const userId = useSelector(getUserId)!;
  const isAuthenticated = Boolean(useSelector(getIsAuthenticated));
  const isDisabled = Boolean(useSelector(isSearchAlertDisabled));
  const hasAlert = useSelector(hasSearchAlert);
  const totalNumberOfListings = useSelector(getNumberOfListings);
  const cityCanonical = useSelector(getCityCanonical);
  const cityNameLocalized = useSelector(getCityName);
  const currentAlert = useSelector(getSearchAlert) || undefined;

  const showCreateSearchAlert = Boolean(location.query.showCreateSearchAlert);

  const [dialogType, setDialogType] = useState<DialogType>(
    showCreateSearchAlert ? 'main' : undefined,
  );

  const handleCreateAlertClick = React.useCallback(() => {
    if (isAuthenticated) {
      return setDialogType('main');
    }

    const returnUrl = `${location.pathname}?${qs.stringify({
      ...location.query,
      showCreateSearchAlert: 1,
    })}`;

    dispatch(NavbarActions.showModal('register', returnUrl));
  }, [isAuthenticated, location, dispatch]);

  React.useEffect(() => {
    // trigger the search alert dialog when the user wants to edit an alert and
    // the filters have been changed in the mobile filters modal.
    if (!md && hasAlert && !isDisabled && location.query?.updateSearchAlert) {
      handleCreateAlertClick();
    }
  }, [
    md,
    hasAlert,
    isDisabled,
    handleCreateAlertClick,
    location.query?.updateSearchAlert,
  ]);

  const onDialogDismiss = React.useCallback(() => {
    setDialogType(undefined);
    removeUpdateSearchAlertParam();
  }, [removeUpdateSearchAlertParam]);

  const onDialogSubmit = React.useCallback(
    async (
      title: string,
      channel: SearchAlertChannel,
      frequency: SearchAlertFrequency,
      newUserPhone?: PhoneNumberProps,
    ) => {
      if (!channel || !frequency || !title) {
        return;
      }

      try {
        if (newUserPhone) {
          const newUserData = {
            phone: newUserPhone.phone,
            phoneCountryCode: newUserPhone.code,
          };

          await dispatch(updateUser(userId, newUserData));
        }
        const last24hoursOnly = channel === 'whatsapp';

        await dispatch(
          upsertSearchAlert(title, frequency, channel, last24hoursOnly),
        );
      } catch (err) {
        setDialogType(undefined);
        const errors = err?.cause?.response?.errors;
        dispatch(
          showAlert({
            text:
              errors?.[0].key === 'phone'
                ? T('incorrect phone number, validation failed')
                : T('We could not save your changes. Please try again!'),
            kind: AlertType.Danger,
            dismissAfter: 3000,
          }),
        );
        return;
      }

      trackEvent('search alert channel submitted', {
        channel,
      });

      removeUpdateSearchAlertParam();
      setDialogType(!hasAlert ? 'response-create' : 'response-update');
    },
    [dispatch, trackEvent, removeUpdateSearchAlertParam, hasAlert],
  );

  const onResponseDialogDismiss = React.useCallback(() => {
    setDialogType(undefined);
  }, []);

  const defaultSize = md ? 'medium' : 'small';

  const label = hasAlert
    ? T('search_alert_update_button')
    : T('search_alert_create_button');

  const handleLinkOnClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      if (!isDisabled) {
        handleCreateAlertClick();
      }
    },
    [handleCreateAlertClick, isDisabled],
  );

  return (
    <NoSSR>
      <Track
        name="Clicked create search alert"
        type="button"
        getDynamicData={() => ({
          resultCount: totalNumberOfListings,
          cityCanonical,
        })}
      >
        {variant === 'link' ? (
          <Link
            to=""
            onClick={handleLinkOnClick}
            data-test-locator="Search Alert Button - Link"
            className={isDisabled ? classes.linkDisabled : classes.link}
          >
            <AlarmBell1Light
              className={cx(
                classes.linkIcon,
                isDisabled && classes.linkIconDisabled,
              )}
            />
            {` ${label}`}
          </Link>
        ) : (
          <Button
            color="secondary"
            variant="outlined"
            onClick={handleCreateAlertClick}
            disabled={isDisabled}
            size={size ?? defaultSize}
            data-test-locator="Search Alert Button"
            startIcon={<AddAlertIcon />}
            fullWidth={fullWidth}
          >
            {label}
          </Button>
        )}
      </Track>
      <SearchAlertDialog
        modalType={hasAlert ? 'update' : 'create'}
        isOpen={dialogType === 'main'}
        onSubmit={onDialogSubmit}
        onClose={onDialogDismiss}
        alert={currentAlert}
        defaultTitle={cityNameLocalized}
      />
      <SearchAlertResponseDialog
        isOpen={
          dialogType === 'response-create' || dialogType === 'response-update'
        }
        hasAlert={dialogType === 'response-update'}
        onClose={onResponseDialogDismiss}
      />
    </NoSSR>
  );
};
